import React, { useEffect, useMemo, useRef, useState } from "react";
import {
	SetDigitalTwinsPageNumber,
	SetDigitalTwinsTabValue,
} from "../../Actions/DigitalTwinsList";
import PageHeaderKPI from "../../Features/PageHeader/PageHeaderKPI";
import { Box } from "@mui/material";
import { notification } from "antd";
import { InnerBox, OuterBoxV2 } from "../../Components/CustomBox";
import { useDispatch, useSelector } from "react-redux";
import {
	decryptTheParams,
	encryptTheParams,
} from "../../Helper/QueryParams/EncryptDecrypt";
import { useNavigate } from "react-router-dom";
import { ThemeProperties } from "../../Theme/ThemeProperties";
import BasicTabPanel from "../../Features/Tabs/BasicTabPanel";
import TabWithColorDigitalTwins from "../../Features/Tabs/TabWithColorDigitalTwins";
import AddBatteryDialog from "../../Features/Dialog/DigitalTwin/AddBatteryConfigDialog";
import Events from "../../Analytics/Events";
import { getDigitalTwinConfigList } from "../../Api/DigitalTwinListApi";
import { SetSessionExpired } from "../../Actions";
import DigitalTwinListTab from "./DigitalTwinListTab";
import BatteryPackListTab from "./BatteryPackListTab";
import DigitalTwinConfigListTab from "./DigitalTwinConfigListTab";
import UnlinkedIOTDevicesTab from "./UnlinkedIOTDevicesTab";
import { getConnectedBatteries } from "../../Api/Api";
import { getUnlinkedIotList } from "../../Api/IoTApi";
import { getBatteryPackList } from "../../Api/BatteryPackApi";
import { useTranslation } from 'react-i18next';
import { getOrganizationLangSettings } from "../../Helper/Organization/Functions";
import { isAdmin } from "../../Helper/Common/UserRole";
import { usePermissions } from "../../Context/PermissionContext";
import { useCanUseGlobalAdvancedFilter } from "../../Helper/Permissions/GlobalAdvancedFilter";
import { FEATURE_CODES } from "../../Static/FeatureMapping";
import CODE from "../../Static/Constants/StatusCodes";
import AppCache from "../../Helper/Common/AppCache";
import { getTabCountKey } from "../../Helper/Inventory/inventoryLoadHelper";
import { getRbacFleets, getRbacLocations } from "../../Api/RbacApi";
import { restoreFleetSelection } from "../../Hooks/useGlobalFilterData";
import {
	isIsolatedFilterRoute,
	readGlobalFilterFromUrl,
	stripGlobalFilterFromUrl,
	transformOnboardingLocationsForHierarchy,
} from "../../PagesContent/Overview/CommonFunctions";
import GlobalFilter from "../../PagesContent/Overview/components/GlobalFilter";
import OperationalStatusMultiSelect from "../../PagesContent/Common/OperationalStatusMultiSelect";

const Context = React.createContext({
	name: "Default",
});

export default function InventoryMain(props) {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { t } = useTranslation();
	const { hasPermission } = usePermissions();
	const canUseGlobalAdvancedFilter = useCanUseGlobalAdvancedFilter();
	const queryParams = decryptTheParams();

	const [api, contextHolder] = notification.useNotification();
	const tabvalue = useSelector((state) => state.DigitalTwinsTabValue.value);

	const [mounted, setMounted] = useState(true);
	const [selectedFilters, setSelectedFilters] = useState(queryParams.filters ? JSON.parse(queryParams.filters) : {})

	// Global filter state
	const [allFleets, setAllFleets] = useState([]);
	const [selectedFleets, setSelectedFleets] = useState([]);
	// This page keeps its own copy of the filter plumbing rather than using
	// useGlobalFilterData, because of the globalFilterReady gate below. It still
	// shares the SELECTION, so arriving here from Overview keeps the filter.
	// The selection travels on links only: read from the URL the link opened,
	// then consumed so a reload starts clean. Arriving here from the nav rail
	// applies nothing.
	const carriedSelection = useMemo(
		() => (isIsolatedFilterRoute(window.location.pathname) ? null : readGlobalFilterFromUrl()),
		[]
	);
	useEffect(() => {
		if (carriedSelection) stripGlobalFilterFromUrl();
	}, [carriedSelection]);
	const [allOnboardingLocationsHierarchy, setAllOnboardingLocationsHierarchy] = useState([]);
	const [globalFilters, setGlobalFilters] = useState({});
	const [globalFilterReady, setGlobalFilterReady] = useState(false);
	// Whether the fleet list has come back at all. Distinct from "is non-empty" -- see
	// fetchFleets.
	const [fleetsResolved, setFleetsResolved] = useState(false);
	const locationsRequestRef = useRef(null);
	const globalFilterConfig = AppCache.getItem("globalFilter")?.find((f) => f.type === "unifiedGlobalFilter") || null;

	const newColumns = useSelector((state) => state.DigitalTwinsColumns.value);
	const [connectedBatteries, setConnectedBatteries] = useState(0);
	const [totalConfigs, setTotalConfigs] = useState(0);
	const [totalBatteryPacks, setTotalBatteryPacks] = useState(0);
	const [totalUnlinkedDevices, setTotalUnlinkedDevices] = useState(0);

	// Start empty to prevent expensive initial API call
	// setupTabColumns() in tab components will populate with correct columns
	const [visibleColumnKeys, setVisibleColumnKeys] = useState({
		0: [],
		1: [],
		2: [],
		3: []
	});

	const sortOrder = queryParams.sorting ? JSON.parse(queryParams.sorting) : { sortBy: 'lastGPSPing', orderBy: 'desc' };
	const [sorting, setSorting] = useState([sortOrder])

	const [searchTerm, setSearchTerm] = useState({
		selectedDevices: [],
		selectedSearchColumn: null
	})

	const applySelectedFilters = (selectedFilters) => {
		dispatch(SetDigitalTwinsPageNumber(1))
		let temp = {
			...queryParams,
			filters: JSON.stringify({ ...selectedFilters })
		};

		encryptTheParams(temp, navigate);
		setSelectedFilters(selectedFilters)
	}

	const setupTotalConfigurations = async () => {
		// rowsPerPage: 1 because only `total` is read. Without it getDigitalTwinConfigList
		// defaults to pageCount 20 (DigitalTwinListApi.js), fetching 20 full config rows
		// and throwing all of them away. The other two count fetches already do this.
		const res = await getDigitalTwinConfigList({ rowsPerPage: 1 })
		if (res && res.responseStatus.code === 200) {
			setTotalConfigs(res?.response?.total?.total)
		}
	}

	const setupTotalBatteryPacks = async () => {
		const res = await getBatteryPackList({ pageNumber: 1, rowsPerPage: 1 })
		if (res && res.responseStatus.code === 200) {
			setTotalBatteryPacks(res?.response?.total?.total)
		}
	}

	const setupTotalUnlinkedDevices = async () => {
		const res = await getUnlinkedIotList({ pageNumber: 1, rowsPerPage: 1 })
		if (res && res.responseStatus.code === 200) {
			setTotalUnlinkedDevices(res?.response?.total?.total)
		}
	}

	// Reports only that the fleet list has SETTLED -- succeeded, came back empty, or
	// failed. Whether that releases globalFilterReady is decided in one place, the
	// effect below, so an empty list and a failed request cannot take different paths.
	//
	// It matters that "settled" is distinct from "non-empty": on mount selectedFleets is
	// already [], so an effect that treated empty as "nothing to scope by" would release
	// the gate before the fleets had even been requested, which is the whole thing the
	// gate exists to prevent.
	const fetchFleets = async () => {
		try {
			const fleetResponse = await getRbacFleets();
			const formattedFleets = (fleetResponse?.response?.data || []).map((fleet) => ({
				id: fleet.fleetID,
				name: fleet.fleetName,
				category: fleet.category,
			}));
			setAllFleets(formattedFleets);
			// Not `formattedFleets`: defaulting to every fleet is what discarded
			// the selection the user arrived with.
			setSelectedFleets(restoreFleetSelection(formattedFleets, carriedSelection?.fleetIds));
		} catch (error) {
			// Swallowed on purpose: an unscoped list beats no list. The backend applies
			// its own scoping regardless of the headers.
		} finally {
			setFleetsResolved(true);
		}
	};

	const formatOnboardingLocations = (locations) => {
		if (!locations || locations.length === 0) return { countries: [], states: [], cities: [], depots: [] };
		const sets = { countries: new Set(), states: new Set(), cities: new Set(), depots: new Set() };
		locations.forEach(({ originCountry, originState, originCity, originDepo }) => {
			if (originCountry) sets.countries.add(originCountry);
			if (originState) sets.states.add(originState);
			if (originCity) sets.cities.add(originCity);
			if (originDepo) sets.depots.add(originDepo);
		});
		return {
			countries: [...sets.countries],
			states: [...sets.states],
			cities: [...sets.cities],
			depots: [...sets.depots],
		};
	};

	const fetchOnboardingLocations = async (fleets) => {
		const fleetIds = fleets.map((item) => item.id);
		// Operating location is an independent filter: it is never scoped to the
		// customer selection, so the full permitted tree is fetched once and
		// reused for every later call. This is why the fleet ids below are not
		// passed to getRbacLocations - narrowing the tree to the selected
		// customers is what the three way filter split exists to undo.
		try {
			if (!locationsRequestRef.current) {
				locationsRequestRef.current = getRbacLocations([]);
			}
			const response = await locationsRequestRef.current;
			// GetData answers a failure with a well formed envelope rather than
			// throwing, so caching one would leave the filter empty for the life of
			// the mount. Drop it and let the next call retry.
			if (response?.responseStatus?.code !== CODE.SUCCESS) locationsRequestRef.current = null;
			const locations = response?.response?.data || [];
			const hierarchyLocations = transformOnboardingLocationsForHierarchy(locations);
			const flatLocations = formatOnboardingLocations(locations);
			setAllOnboardingLocationsHierarchy(hierarchyLocations);
			setGlobalFilters(prev => ({
				...prev,
				fleets: fleetIds,
				onboardingLocations: flatLocations,
				allOnboardingLocations: flatLocations,
				// Carried in from the previous page, but only against a tree that
				// actually arrived. Over an empty tree - a failed locations
				// response - GlobalFilter reads the value as stale and clears it,
				// which costs a second mis-scoped list fetch that lastFetchParams
				// does not dedupe, and wipes the filter on every other page too.
				...(hierarchyLocations.length && carriedSelection?.hierarchicalLocation
					? { hierarchicalLocation: carriedSelection.hierarchicalLocation }
					: {}),
			}));
		} finally {
			// finally, not the happy path: a throw here would otherwise strand the gate
			// and the list would never load. This is also what reopens it after an Apply
			// re-closed it to suppress the mis-scoped intermediate fetch.
			setGlobalFilterReady(true);
		}
	};

	const handleGlobalFilterChange = ({ fleetIds, hierarchicalLocation }) => {
		const currentFleetIds = globalFilters.fleets || [];
		const fleetsChanged = JSON.stringify([...fleetIds].sort()) !== JSON.stringify([...currentFleetIds].sort());
		if (fleetsChanged) {
			// Changing the fleet set publishes globalFilters TWICE: once below with the
			// new fleets but the PREVIOUS fleets' onboardingLocations, and again when
			// fetchOnboardingLocations returns with the matching locations. Left alone
			// that is two list requests per Apply, the first one mis-scoped -- the same
			// bug the mount-time gate fixes, on the interactive path.
			//
			// Re-closing the gate suppresses the first. It reopens in the effect above,
			// on every route including a failed location fetch, so this cannot strand.
			setGlobalFilterReady(false);
			const selectedFleetObjects = allFleets.filter((f) => fleetIds.includes(f.id));
			setSelectedFleets(selectedFleetObjects);
		}
		setGlobalFilters(prev => ({ ...prev, fleets: fleetIds, hierarchicalLocation }));
	};

	const handleOperationalStatusSelection = (selectedValues) => {
		setGlobalFilters(prev => ({ ...prev, operationalStatus: selectedValues }));
	};

	const setupConnectedBatteries = async () => {
		// The page scope, not {}. This count is the chip beside the All Vehicles
		// title, so it has to describe the list under it rather than the whole org.
		const res = await getConnectedBatteries(globalFilters, 'inventory.digitalTwinListTable')
		if (res.responseStatus.code === 200) {
			setConnectedBatteries(res.response.data[0]["count"]);
		} else if (res.responseStatus.code === -2) {
			dispatch(SetSessionExpired(true));
		}
	}

	// Each tab's count feeds the chip beside ITS OWN table title, so only the visible
	// tab's number is ever on screen. Fetching all four on mount cost three requests for
	// tabs the user may never open -- including ones hidden by tabsToShow or RBAC.
	//
	// Keyed on tabvalue, which is the positional index the BasicTabPanels use, so landing
	// directly on a tab via the query param fetches that tab's count and no other.
	//
	// The ref guard makes each at most once per page load. It is set before the await
	// rather than after, so a failed count does not retry on every tab switch -- the same
	// single-attempt behaviour these had when they ran on mount.
	const fetchedTabCounts = useRef(new Set());

	// Keyed on the tab AND the scope its count describes, so applying a filter
	// refetches while switching back to a tab already counted under the same scope
	// still does not. Content, not identity: globalFilters is rebuilt on every
	// change and an identity key would refetch on every render.
	const globalFiltersKey = JSON.stringify(globalFilters);

	useEffect(() => {
		// Only the vehicles count is scoped, so only it needs the gate; the other
		// three send no filters and are unaffected by it either way. Waiting avoids
		// counting the whole org first and correcting it a moment later, which is
		// visible as the number jumping.
		if (canUseGlobalAdvancedFilter && !globalFilterReady) return;

		const countKey = `${tabvalue}:${globalFiltersKey}`;
		if (fetchedTabCounts.current.has(countKey)) return;
		fetchedTabCounts.current.add(countKey);

		const loaders = {
			connectedBatteries: setupConnectedBatteries,
			configs: setupTotalConfigurations,
			batteryPacks: setupTotalBatteryPacks,
			unlinkedDevices: setupTotalUnlinkedDevices,
		};
		loaders[getTabCountKey(tabvalue)]?.();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [tabvalue, globalFiltersKey, globalFilterReady, canUseGlobalAdvancedFilter])

	useEffect(() => {
		if (canUseGlobalAdvancedFilter) {
			fetchFleets();
		}
	}, [])

	// The single place globalFilterReady is released. Every route out of "loading the
	// scope" ends here: fleets present -> resolve locations, then release; fleets absent
	// or the request failed -> release immediately, because nothing further is coming.
	//
	// Also covers the user deselecting every fleet in the filter, which leaves
	// selectedFleets empty with fleetsResolved already true.
	useEffect(() => {
		if (!canUseGlobalAdvancedFilter || !fleetsResolved) return;

		if (selectedFleets.length > 0) {
			fetchOnboardingLocations(selectedFleets);
		} else {
			setGlobalFilterReady(true);
		}
	}, [selectedFleets, fleetsResolved])

	const ChangeTheTab = (newValue) => {
		setMounted(false);
		dispatch(SetDigitalTwinsTabValue(newValue));
		dispatch(SetDigitalTwinsPageNumber(1));
		if (selectedFilters && Object.keys(selectedFilters).length > 0) {
			applySelectedFilters({})
		}

		// Reset search term on tab change
		setSearchTerm({
			selectedDevices: [],
			selectedSearchColumn: null
		});

		const tabNames = ["Batteries", "Configurations", "Battery Packs", "Unmapped IOT Devices"];
		Events(`clicked Digital Twins ${tabNames[newValue] || "Unknown"}`);
		let temp;
		const queryParams = decryptTheParams();
		temp = {
			...queryParams,
			tabValue: newValue,
		};
		encryptTheParams(temp, navigate);
		setMounted(true);
	};

	const orgLangSettings = getOrganizationLangSettings();
	let tab1Name = t(orgLangSettings.digitalTwinLabel);

	// Tab ORDER is the contract: TAB_INDEX constants in the tab components and the
	// positional columnsConfig array in Inventory.jsx are both indexed by position here.
	// Reordering or inserting requires updating all three together.
	//
	// All Battery Packs sits third, not second, on purpose. All Vehicles (0) and Battery
	// Configurations (1) share filterConfigurations.digitalTwinListTable and tell each
	// other apart by these indices; inserting at position 1 would renumber Configurations
	// and silently drop any filter whose `tabs` array said [0, 1] — in every org's config.
	// Appending after Configurations keeps 0 and 1 stable, so no existing config moves.
	//
	// No `count` here: TabWithColorDigitalTwins does not render one. The counts these
	// used to carry are the chips inside each tab's own table header, fed by the
	// totalConfigs / totalBatteryPacks / totalUnlinkedDevices props below.
	let Tabs = [
		{ id: 1, slug: 'digital_twins', name: tab1Name, color: ThemeProperties.red_400 },
		{ id: 2, slug: 'digital_twin_configs', name: t('inventory:tabs.battery_config.label'), color: ThemeProperties.green_400 },
		{ id: 3, slug: 'battery_packs', name: t('inventory:tabs.battery_packs.label', 'All Battery Packs'), color: ThemeProperties.purple },
		{ id: 4, slug: 'unlinked_iot_devices', name: t('inventory:tabs.unlinked_iot_devices.label'), color: ThemeProperties.blue_400 },
	];

	// Filter tabs based on pagesContent configuration
	if (props.pagesContent?.tabsToShow) {
		console.log('[InventoryMain] tabsToShow from backend:', props.pagesContent.tabsToShow);
		Tabs = Tabs.filter(tab => props.pagesContent.tabsToShow.includes(tab.slug));
	}

	// Filter IOT devices tab based on version
	const rbacVersion = AppCache.getItem('rbacVersion') || 'v1';
	const rbacEnabled = AppCache.getItem('rbacEnabled') ?? false;

	// All Battery Packs is gated on the pack-replacement feature itself: an org that has
	// Battery Pack Replacement gets the registry tab, an org that does not, does not.
	// Under RBAC v1 hasPermission() returns true, which is correct — those orgs already
	// see the replacement feature.
	if (!hasPermission(FEATURE_CODES.BATTERY_COMPONENTS, 'view')) {
		Tabs = Tabs.filter(tab => tab.slug !== "battery_packs");
	}

	if (rbacVersion === 'v2' && rbacEnabled) {
		// V2: Use RBAC permission check
		const hasIOTPermission = hasPermission(FEATURE_CODES.IOT_DEVICES, 'view');
		console.log('[InventoryMain] IOT_DEVICES permission check:', hasIOTPermission);
		console.log('[InventoryMain] Tabs before RBAC filter:', Tabs.map(t => t.slug));

		if (!hasIOTPermission) {
			Tabs = Tabs.filter(tab => tab.slug !== "unlinked_iot_devices");
			console.log('[InventoryMain] Filtered out unlinked_iot_devices tab due to missing permission');
		}

		console.log('[InventoryMain] Final tabs:', Tabs.map(t => t.slug));
	} else {
		// V1: Use old isAdmin() check
		if (!isAdmin()) {
			Tabs = Tabs.filter(tab => tab.slug !== "unlinked_iot_devices");
		}
	}

	const contextValue = useMemo(() => ({ name: "Toast" }), []);
	const [openBatteryConfigDialog, setOpenBatteryConfigDialog] = useState(false);

	const handleCloseDialog = () => {
		setOpenBatteryConfigDialog(false);
	};

	return (
		<div>
			<OuterBoxV2>
				<InnerBox>
					<Context.Provider value={contextValue}>
						{contextHolder}
						<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
							<PageHeaderKPI
							name={t('inventory:page_header')}
							skipRightSide={true}
							nonClickableKpis={[]}
							connectedBatteries={0}
							sx={{ flex: 1 }}
						/>
						{tabvalue === 0 && canUseGlobalAdvancedFilter && (
							<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
								{/* TODO: Operational Status global filter - commented until fixed
								<OperationalStatusMultiSelect
									selectedValues={globalFilters?.operationalStatus || []}
									onSelectionChange={handleOperationalStatusSelection}
									onOptionsLoaded={(allValueCodes, defaultSelection) =>
										setGlobalFilters(prev => ({
											...prev,
											operationalStatus: defaultSelection,
											allOperationalStatus: allValueCodes,
										}))
									}
								/>
								*/}
								{globalFilterConfig && globalFilterReady && (
									<GlobalFilter
										allFleets={allFleets}
										allOnboardingLocationsHierarchy={allOnboardingLocationsHierarchy}
										selectedFleetIds={globalFilters?.fleets || []}
										selectedLocations={globalFilters?.hierarchicalLocation}
										onChange={handleGlobalFilterChange}
									/>
								)}
							</Box>
						)}
						</Box>

						<Box sx={{
							display: "flex",
							position: 'relative',
							justifyContent: "space-between",
							alignItems: "center",
							mt: 2,
							mb: 2,
							borderBottom: "1px solid #E4E3E3",
							marginLeft: '-2rem',
							marginRight: '-2rem'
						}}>
							<TabWithColorDigitalTwins
								ChangeTheTab={ChangeTheTab}
								tabs={Tabs}
							/>
						</Box>

						<BasicTabPanel value={tabvalue} index={0} key={0}>
							{
								mounted && <DigitalTwinListTab
									selectedFilters={selectedFilters}
									setSelectedFilters={applySelectedFilters}
									sorting={sorting}
									setSorting={setSorting}
									searchTerm={searchTerm}
									setSearchTerm={setSearchTerm}
									visibleColumnKeys={visibleColumnKeys}
									setVisibleColumnKeys={setVisibleColumnKeys}
									pagesContent={props.pagesContent}
									permissions={props?.permissions}
									navigate={navigate}
									columns={newColumns}
									connectedBatteries={connectedBatteries}
									onTagsUpdate={setupConnectedBatteries}
								globalFilters={globalFilters}
								allFleets={allFleets}
								allOnboardingLocationsHierarchy={allOnboardingLocationsHierarchy}
								globalFilterConfig={globalFilterConfig}
								canUseGlobalAdvancedFilter={canUseGlobalAdvancedFilter}
								globalFilterReady={globalFilterReady}
								onGlobalFilterChange={handleGlobalFilterChange}
								/>
							}
						</BasicTabPanel>

						<BasicTabPanel value={tabvalue} index={1} key={1}>
							{
								mounted && <DigitalTwinConfigListTab
									selectedFilters={selectedFilters}
									setSelectedFilters={applySelectedFilters}
									sorting={sorting}
									setSorting={setSorting}
									visibleColumnKeys={visibleColumnKeys}
									setVisibleColumnKeys={setVisibleColumnKeys}
									pagesContent={props.pagesContent}
									permissions={props?.permissions}
									navigate={navigate}
									columns={newColumns}
									totalConfigs={totalConfigs}
									setOpenBatteryConfigDialog={setOpenBatteryConfigDialog}
								/>
							}
						</BasicTabPanel>

						<BasicTabPanel value={tabvalue} index={2} key={2}>
							{
								mounted && <BatteryPackListTab
									selectedFilters={selectedFilters}
									setSelectedFilters={applySelectedFilters}
									sorting={sorting}
									setSorting={setSorting}
									searchTerm={searchTerm}
									setSearchTerm={setSearchTerm}
									visibleColumnKeys={visibleColumnKeys}
									setVisibleColumnKeys={setVisibleColumnKeys}
									pagesContent={props.pagesContent}
									permissions={props?.permissions}
									navigate={navigate}
									columns={newColumns}
									totalBatteryPacks={totalBatteryPacks}
								/>
							}
						</BasicTabPanel>

						<BasicTabPanel value={tabvalue} index={3} key={3}>
							{
								mounted && <UnlinkedIOTDevicesTab
									selectedFilters={selectedFilters}
									setSelectedFilters={applySelectedFilters}
									sorting={sorting}
									setSorting={setSorting}
									searchTerm={searchTerm}
									setSearchTerm={setSearchTerm}
									visibleColumnKeys={visibleColumnKeys}
									setVisibleColumnKeys={setVisibleColumnKeys}
									pagesContent={props.pagesContent}
									permissions={props?.permissions}
									navigate={navigate}
									columns={newColumns}
									totalUnlinkedDevices={totalUnlinkedDevices}
								/>
							}
						</BasicTabPanel>
					</Context.Provider>
				</InnerBox>
			</OuterBoxV2>
			<AddBatteryDialog
				open={openBatteryConfigDialog}
				handleCloseDialog={handleCloseDialog}
			/>
		</div>
	);
}
